import styled from '@emotion/styled';
import React from 'react';
import { format, isSameMonth } from 'date-fns';
import { useCurrentLocale } from '../../../../../context/I18n';
import { getDateFnsLocaleByString } from '../../../../../utils/dateUtils';
import { useIsMdScreen } from '../../../../../hooks/useIsScreenSizeLargerThan';
import { Theme } from '../../../../../theme';
import { Nullable } from '../../../../../utils/nullable';
import { CalendarDay } from '../../AbsenceCalendar/types';
import DaysOfMonth from './DaysOfMonth';
import LoadingDaysOfMonth from './LoadingDaysOfMonth';
import { CalendarMonth, MonthRowProps } from './types';

type MonthNameTableHeaderProps = {
  isHovered?: boolean;
  isCurrent?: boolean;
};

type MonthNameProps = {
  hoveredDay?: Nullable<CalendarDay>;
  month: CalendarMonth;
};

const MonthNameTableHeader = styled.th<MonthNameTableHeaderProps>(
  {
    position: 'sticky',
    left: 0,
    zIndex: 999,
    backgroundColor: `${Theme.color.white} !important`,
    fontSize: `${Theme.font.size.base} !important`,
    fontWeight: 'normal',
  },
  ({ isHovered, isCurrent }) => {
    return {
      color: isHovered ? Theme.color.primary : undefined,
      '::after': isCurrent
        ? {
            content: "''",
            position: 'absolute',
            borderBottom: `3px solid ${Theme.color.primary}`,
            bottom: 0,
            left: 0,
            right: 0,
          }
        : undefined,
    };
  },
);

const MonthName: React.FC<React.PropsWithChildren<MonthNameProps>> = ({
  month,
  hoveredDay,
}) => {
  const locale = useCurrentLocale();
  const dateFnsLocale = getDateFnsLocaleByString(locale);
  const monthDate = month.month.toDate();
  const isCurrentMonth = isSameMonth(new Date(), monthDate);
  const isHoveredMonth = !!(
    hoveredDay && isSameMonth(hoveredDay.day.toDate(), monthDate)
  );
  const isMdScreen = useIsMdScreen();

  const monthName = format(monthDate, isMdScreen ? 'MMMM' : 'MMM', {
    locale: dateFnsLocale,
  });

  return (
    <MonthNameTableHeader isHovered={isHoveredMonth} isCurrent={isCurrentMonth}>
      {monthName}
    </MonthNameTableHeader>
  );
};

const MonthRow: React.FC<React.PropsWithChildren<MonthRowProps>> = ({
  month,
  loading,
  onHover,
  hoveredDay,
}) => {
  return (
    <tr>
      <MonthName month={month} hoveredDay={hoveredDay} />
      {loading ? (
        <LoadingDaysOfMonth month={month.month} />
      ) : (
        <DaysOfMonth
          days={month.days}
          daysInMonth={month.daysInMonth}
          onHover={onHover}
          hoveredDay={hoveredDay}
        />
      )}
    </tr>
  );
};

export default MonthRow;
